import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { Save, Play, ArrowLeft, Settings2, Search, ChevronRight, ChevronDown, Plus, Hash, Calendar, Type, ToggleLeft, Database, GripVertical, X, Download, FileText, FileSpreadsheet, FileType, Table2, BarChart3, Map, Grid3X3, RefreshCw } from 'lucide-react';
import { useTheme } from '@ui-admin/contexts/ThemeContext';
import type { ReportDefinitionLocal, ReportConfig, ReportColumn, ReportFormat } from '@firesquirrel/local-db';
import { getReportDefinition, updateReportDefinition, createReportDefinition } from '../../services/systemReportDefinitionService';
import { getAvailableFields, fetchPreviewData, getCollectionCounts, getSchemaFieldsForAI, getSchemaPreviewForAI, type FieldDefinition } from '../../services/systemReportDataService';

const DEFAULT_REPORT_CONFIG: ReportConfig = {
  format: 'tabular',
  dataSources: [],
  columns: [],
  groupBy: [],
  subtotals: false,
  grandTotal: false,
};

export default function SystemDataReportBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { setBreadcrumbLabel } = useBreadcrumbs();
  const isNew = !id || id === 'new';
  
  // Detect if this is an AI-generated template from navigation state or loaded report
  const aiTemplateData = location.state as { aiGenerated?: boolean; previewConfig?: any } | null;
  const [isAIGenerated, setIsAIGenerated] = useState<boolean>(aiTemplateData?.aiGenerated || false);

  const [report, setReport] = useState<ReportDefinitionLocal | null>(null);
  const [reportConfig, setReportConfig] = useState<ReportConfig>(DEFAULT_REPORT_CONFIG);
  const [reportName, setReportName] = useState('Untitled Report');
  const [reportDescription, setReportDescription] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<ReportColumn[]>([]);
  const [activePanel, setActivePanel] = useState<'palette' | 'properties'>('palette');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['Departments', 'Organizations']));
  const [fieldCatalog, setFieldCatalog] = useState<FieldDefinition[]>([]);
  const [collectionCounts, setCollectionCounts] = useState<Record<string, number>>({});
  const [previewData, setPreviewData] = useState<Record<string, any>[]>([]);
  const [loadingFields, setLoadingFields] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    if (!isNew && id) {
      loadReport(id);
    } else if (isNew && aiTemplateData?.previewConfig) {
      // Pre-populate with AI-generated config
      const config = aiTemplateData.previewConfig;
      setReportName(config.name || 'Untitled Report');
      setReportDescription(config.description || '');
      if (config.reportConfig) {
        setReportConfig(config.reportConfig);
        // Columns are in reportConfig.columns, not config.columns
        if (config.reportConfig.columns && Array.isArray(config.reportConfig.columns)) {
          setSelectedColumns(config.reportConfig.columns);
        }
      }
      // Mark as AI-generated so preview uses mock data
      setIsAIGenerated(true);
    }
  }, [id, isNew, aiTemplateData]);

  // Load field catalog - use schema fields for AI-generated templates, system admin fields otherwise
  useEffect(() => {
    async function loadFieldCatalog() {
      setLoadingFields(true);
      try {
        if (isAIGenerated) {
          // AI-generated template: fetch L1 Schema Registry fields
          const fields = await getSchemaFieldsForAI();
          setFieldCatalog(fields);
          setCollectionCounts({}); // No actual data counts for schema fields
        } else {
          // Manual template: use system admin fields from Dexie
          const [fields, counts] = await Promise.all([
            getAvailableFields(),
            getCollectionCounts(),
          ]);
          setFieldCatalog(fields);
          setCollectionCounts(counts);
        }
      } catch (err) {
        console.error('[SystemDataReportBuilderPage] Failed to load field catalog:', err);
      } finally {
        setLoadingFields(false);
      }
    }
    loadFieldCatalog();
  }, [isAIGenerated]);

  // Fetch preview data when columns change - placeholder for AI templates, real data for manual
  useEffect(() => {
    if (selectedColumns.length === 0) {
      setPreviewData([]);
      return;
    }

    let cancelled = false;
    async function loadPreviewData() {
      setLoadingPreview(true);
      try {
        const columnFields = selectedColumns.map((c) => c.field);
        const data = isAIGenerated 
          ? await getSchemaPreviewForAI(columnFields)
          : await fetchPreviewData(columnFields, 50);
        if (!cancelled) {
          setPreviewData(data);
        }
      } catch (err) {
        console.error('[SystemDataReportBuilderPage] Failed to fetch preview data:', err);
      } finally {
        if (!cancelled) {
          setLoadingPreview(false);
        }
      }
    }
    loadPreviewData();
    return () => { cancelled = true; };
  }, [selectedColumns, isAIGenerated]);

  async function loadReport(reportId: string) {
    setLoading(true);
    try {
      const data = await getReportDefinition(reportId);
      if (data) {
        setReport(data);
        setReportName(data.name);
        setReportDescription(data.description || '');
        setReportConfig(data.reportConfig || DEFAULT_REPORT_CONFIG);
        setSelectedColumns(data.reportConfig?.columns || []);
        if (data.name) setBreadcrumbLabel(`/data-report-templates/${reportId}`, data.name);
        
        // Detect if this is an AI-generated template
        // Method 1: Check explicit marker
        let isAITemplate = (data as any).aiGenerationSource === 'ai-generated';
        
        // Method 2 (fallback): If report references tenant data collections in system-web, it's AI-generated
        // (system-web has no real tenant data, so these must be schema-based AI templates)
        if (!isAITemplate && data.reportConfig?.dataSources) {
          const tenantCollections = ['inspectionSeries', 'cases', 'entities', 'violations', 'contacts', 'inspectionAccounts'];
          const hasTenantData = data.reportConfig.dataSources.some((ds: any) => 
            tenantCollections.includes(ds.collection)
          );
          if (hasTenantData) {
            isAITemplate = true;
          }
        }
        
        setIsAIGenerated(isAITemplate);
      }
    } catch (error) {
      console.error('[SystemDataReportBuilderPage] Failed to load report:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const reportData = {
        name: reportName || 'Untitled Report',
        description: reportDescription,
        type: 'report' as const,
        status: report?.status || ('draft' as const),
        reportConfig: { ...reportConfig, columns: selectedColumns },
        scope: report?.scope || { level: 'system' as const },
        sharing: report?.sharing || { sharedWith: [{ type: 'all-below' as const, fromLevel: 'system' as const }], isReadOnly: false },
        createdBy: report?.createdBy || 'system-admin',
        updatedBy: 'system-admin',
        // Preserve AI generation marker so mock data is used on reload
        ...(isAIGenerated && { aiGenerationSource: 'ai-generated' as const }),
      };

      if (isNew) {
        const newReport = await createReportDefinition(reportData as any);
        navigate(`/data-report-templates/${newReport._id}`, { replace: true });
      } else if (id) {
        await updateReportDefinition(id, reportData);
      }
    } catch (error) {
      console.error('[SystemDataReportBuilderPage] Failed to save report:', error);
    } finally {
      setSaving(false);
    }
  }, [reportName, reportDescription, reportConfig, selectedColumns, report, isNew, id, navigate, isAIGenerated]);

  const handleAddColumn = (field: string, label: string, type: string) => {
    if (!selectedColumns.find((c) => c.field === field)) {
      setSelectedColumns((prev) => [...prev, { field, label, visible: true, format: type === 'number' ? 'number' : type === 'date' ? 'date' : 'text' }]);
    }
  };

  const handleRemoveColumn = (field: string) => {
    setSelectedColumns((prev) => prev.filter((col) => col.field !== field));
  };

  if (loading) {
    return <div className="h-full flex items-center justify-center"><div className="text-gray-500">Loading report...</div></div>;
  }

  // Build categories from field catalog
  const categories: Record<string, FieldDefinition[]> = {};
  fieldCatalog.forEach((f: FieldDefinition) => {
    if (!categories[f.category]) categories[f.category] = [];
    categories[f.category].push(f);
  });

  const getFilteredCategories = (): Record<string, FieldDefinition[]> => {
    if (!searchQuery.trim()) return categories;
    const filtered: Record<string, FieldDefinition[]> = {};
    Object.entries(categories).forEach(([cat, fields]: [string, FieldDefinition[]]) => {
      const matching = fields.filter((f: FieldDefinition) => f.label.toLowerCase().includes(searchQuery.toLowerCase()));
      if (matching.length > 0) filtered[cat] = matching;
    });
    return filtered;
  };
  const filteredCategories = getFilteredCategories();

  // Get count for a category based on its fields' collection prefix
  const getCategoryCount = (category: string): number | undefined => {
    const fields = categories[category];
    if (!fields || fields.length === 0) return undefined;
    const prefix = fields[0].field.split('.')[0];
    return collectionCounts[prefix];
  };

  const hasPreviewRows = previewData.length > 0;

  return (
    <div className="h-full flex flex-col" style={{ background: theme.pageBg }}>
      <header className="shrink-0 flex items-center justify-between px-4 py-3 border-b" style={{ background: theme.headerBg, borderColor: theme.headerBorder }}>
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/data-report-templates')} className="p-2 rounded-lg hover:bg-gray-100"><ArrowLeft className="h-5 w-5 text-gray-600" /></button>
          <div>
            <input type="text" value={reportName} onChange={(e) => setReportName(e.target.value)} className="text-lg font-semibold bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1" placeholder="Report Name" />
            <input type="text" value={reportDescription} onChange={(e) => setReportDescription(e.target.value)} className="block text-sm text-gray-500 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1 w-full" placeholder="Add description..." />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSave} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: theme.accent }}>
            <Save className="h-4 w-4" />{saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <aside className="w-72 shrink-0 border-r overflow-y-auto" style={{ background: theme.cardBg, borderColor: theme.cardBorder }}>
          <div className="p-3 border-b" style={{ borderColor: theme.cardBorder }}>
            <div className="flex gap-1">
              <button onClick={() => setActivePanel('palette')} className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg ${activePanel === 'palette' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}>Data Fields</button>
              <button onClick={() => setActivePanel('properties')} className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg ${activePanel === 'properties' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}><Settings2 className="h-3 w-3 inline mr-1" />Properties</button>
            </div>
          </div>
          {activePanel === 'palette' ? (
            <div className="flex flex-col h-full">
              {loadingFields ? (
                <div className="flex flex-col h-full items-center justify-center">
                  <RefreshCw className="h-5 w-5 text-gray-400 animate-spin" />
                  <span className="text-xs text-gray-500 mt-2">Loading fields...</span>
                </div>
              ) : (
                <>
                  <div className="p-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search fields..." className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto px-2 pb-3">
                    {Object.entries(filteredCategories).map(([category, fields]: [string, FieldDefinition[]]) => (
                      <div key={category} className="mb-1">
                        <button onClick={() => setExpandedCategories((prev) => { const next = new Set(prev); next.has(category) ? next.delete(category) : next.add(category); return next; })} className="w-full flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-lg">
                          {expandedCategories.has(category) ? <ChevronDown className="h-3.5 w-3.5 text-gray-500" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-500" />}
                          <Database className="h-3.5 w-3.5 text-gray-500" />
                          <span className="flex-1 text-left">{category}</span>
                          <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded" title={`${getCategoryCount(category) ?? 0} records`}>
                            {fields.length} fields{getCategoryCount(category) !== undefined ? ` · ${getCategoryCount(category)} rows` : ''}
                          </span>
                        </button>
                        {expandedCategories.has(category) && (
                          <div className="ml-4 mt-0.5 space-y-0.5">
                            {fields.map((field: FieldDefinition) => {
                              const isSelected = selectedColumns.some((c) => c.field === field.field);
                              const TypeIcon = field.type === 'number' ? Hash : field.type === 'date' ? Calendar : Type;
                              return (
                                <button key={field.field} onClick={() => handleAddColumn(field.field, field.label, field.type)} disabled={isSelected} className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-lg ${isSelected ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}>
                                  <TypeIcon className="h-3.5 w-3.5 shrink-0" /><span className="flex-1 text-left truncate">{field.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="p-3">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Columns ({selectedColumns.length})</label>
              {selectedColumns.length === 0 ? (
                <div className="text-center py-6 text-sm text-gray-500">No columns added yet.</div>
              ) : (
                <div className="space-y-1">
                  {selectedColumns.map((col) => (
                    <div key={col.field} className="flex items-center gap-2 px-2 py-2 bg-white border border-gray-200 rounded-lg group">
                      <GripVertical className="h-4 w-4 text-gray-300" />
                      <div className="flex-1 min-w-0"><div className="text-sm font-medium text-gray-700 truncate">{col.label}</div></div>
                      <button onClick={() => handleRemoveColumn(col.field)} className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100"><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </aside>

        <main className="flex-1 overflow-auto p-6">
          {selectedColumns.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4"><Table2 className="h-8 w-8 text-gray-400" /></div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Tabular Report</h3>
              <p className="text-sm text-gray-500 max-w-sm">Add fields from the Data Palette to start building your report.</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2">
                  <Table2 className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Preview</span>
                  <span className="text-xs text-gray-400 ml-auto flex items-center gap-2">
                    {loadingPreview && <RefreshCw className="h-3 w-3 animate-spin" />}
                    {hasPreviewRows && isAIGenerated && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">Sample Data</span>}
                    {hasPreviewRows && !isAIGenerated && <span className="text-green-600">Live</span>}
                    {previewData.length} rows
                  </span>
                </div>
              </div>
              {hasPreviewRows ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead><tr className="bg-gray-50 border-b border-gray-200">{selectedColumns.map((col) => <th key={col.field} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{col.label}</th>)}</tr></thead>
                      <tbody className="divide-y divide-gray-100">{previewData.map((row, i) => <tr key={i} className="hover:bg-gray-50">{selectedColumns.map((col) => <td key={col.field} className="px-4 py-3 text-sm text-gray-700">{row[col.field] || '—'}</td>)}</tr>)}</tbody>
                    </table>
                  </div>
                  {isAIGenerated && (
                    <div className="px-4 py-3 bg-amber-50 border-t border-amber-200">
                      <p className="text-xs text-amber-800">
                        <span className="font-semibold">Note:</span> This preview shows sample data for visualization purposes. 
                        When departments use this template, they will see their actual inspection data.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="px-6 py-10 text-center text-sm text-gray-500">
                  No preview rows are available for the selected fields in system-web.
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
